import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit'
import * as Sentry from '@sentry/nextjs'
import { UploadFile } from 'antd'
import axios from 'axios'
import debug from 'debug'
import {
  quantitativeAnalyticsUploadMutate,
} from 'graphql/analytics/quantitative/upload.quantitative.analytics.mutation'
import { recordUploadMutate } from 'graphql/mutation/record.upload.mutation'
import { ID } from 'utils/id'
import { isAudio } from 'utils/is.audio'
import { isQACompatibleFile } from 'utils/is.quantitative.analytics.compatible.file'
import { AppDispatch } from '../index'
import { MessageInstance } from 'antd/es/message/interface'

const logger = debug('store/upload')

const CancelToken = axios.CancelToken
const cancel = {}

export const uploadTypeValues = [
  'record',
  'quantitative-analytics',
] as const

export type UploadType = typeof uploadTypeValues[number]

export type Upload = {
  id: ID
  name: string
  progress: number
  finished: boolean
  failed: boolean
  instanceId?: ID
  meetingRecordId?: ID
  type?: UploadType
}

const uploadAdapter = createEntityAdapter<Upload>({
  selectId: (upload) => upload.id,
})

export const startRecordUpload = createAsyncThunk<void, {
  message: MessageInstance
  recordId: ID
  name: string
  file: Omit<UploadFile, 'uid'>
  instanceId?: ID
  meetingRecordId?: ID
  type?: UploadType
}, { dispatch: AppDispatch }>(
  'uploads/startRecordUpload',
  async (data, thunkAPI) => {
    const {
      message,
      recordId,
      name,
      file,
      instanceId,
      meetingRecordId,
      type,
    } = data

    if (!isAudio(file)) {
      void message.error('Wrong file format')
      return
    }

    thunkAPI.dispatch(addUpload({
      failed: false,
      finished: false,
      id: recordId,
      instanceId,
      meetingRecordId,
      name,
      progress: 0,
      type,
    }))

    try {
      const {
        data: {
          upload: { uri: signedUrl },
        },
      } = await recordUploadMutate({
        variables: {
          finished: false,
          id: recordId,
        },
      })

      await axios.put(signedUrl, file, {
        cancelToken: new CancelToken(function executor(c) {
          cancel[recordId] = c
        }),
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          thunkAPI.dispatch(updateUpload({
            id: recordId,
            progress: progressEvent.total !== undefined
              ? progressEvent.loaded / progressEvent.total
              : 0,
          }))
        },
      })

      thunkAPI.dispatch(finishUpload(recordId))
    } catch (e) {
      logger('Failed with error: %O', e)
      if (e.message === 'Upload canceled') {
        thunkAPI.dispatch(removeUpload(recordId))
      } else {
        Sentry.captureException(e)
        thunkAPI.dispatch(failUpload(recordId))
      }

      return
    }

    await recordUploadMutate({
      variables: {
        finished: true,
        id: recordId,
      },
    }).catch((e) => {
      Sentry.captureMessage('finished upload but failed to confirm in api')
      Sentry.captureException(e)
      logger('finished upload but failed to confirm in api')
    })
  },
)

export const startQuantitativeAnalyticsUpload = createAsyncThunk<void, {
  message: MessageInstance
  name: string
  file: File
  quantitativeAnalyticsId: ID
}, { dispatch: AppDispatch }>(
  'uploads/startQuantitativeAnalyticsUpload',
  async (data, thunkAPI) => {
    const {
      message,
      quantitativeAnalyticsId,
      name,
      file,
    } = data

    if (!isQACompatibleFile(file)) {
      void message.error('Wrong file format')
      return
    }

    thunkAPI.dispatch(addUpload({
      failed: false,
      finished: false,
      id: quantitativeAnalyticsId,
      name,
      progress: 0,
    }))

    try {
      const {
        data: {
          upload: { url: signedUrl },
        },
      } = await quantitativeAnalyticsUploadMutate({
        variables: {
          finished: false,
          id: quantitativeAnalyticsId,
        },
      })

      await axios.put(signedUrl, file, {
        cancelToken: new CancelToken(function executor(c) {
          cancel[quantitativeAnalyticsId] = c
        }),
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          thunkAPI.dispatch(updateUpload({
            id: quantitativeAnalyticsId,
            progress: progressEvent.total !== undefined
              ? progressEvent.loaded / progressEvent.total
              : 0,
          }))
        },
      })

      thunkAPI.dispatch(finishUpload(quantitativeAnalyticsId))
    } catch (e) {
      logger('Failed with error: %O', e)
      if (e.message === 'Upload canceled') {
        thunkAPI.dispatch(removeUpload(quantitativeAnalyticsId))
      } else {
        Sentry.captureException(e)
        thunkAPI.dispatch(failUpload(quantitativeAnalyticsId))
      }

      return
    }

    await quantitativeAnalyticsUploadMutate({
      variables: {
        finished: true,
        id: quantitativeAnalyticsId,
      },
    }).catch((e) => {
      Sentry.captureMessage('finished upload but failed to confirm in api')
      Sentry.captureException(e)
      logger('finished upload but failed to confirm in api')
    })
  },
)

export const cancelUpload = createAsyncThunk<void, ID>(
  'uploads/cancelUpload',
  (id) => {
    if (cancel[id]) {
      cancel[id]('Upload canceled')
    }
  },
)

export type Uploads = {[key: ID]: Upload}

export const uploadSlice = createSlice({
  initialState: uploadAdapter.getInitialState(),
  name: 'upload',
  reducers: {
    addUpload: uploadAdapter.addOne,
    failUpload: (state, action: PayloadAction<ID>) => {
      state.entities[action.payload].failed = true

      return state
    },
    finishUpload: (state, action: PayloadAction<ID>) => {
      state.entities[action.payload].finished = true

      return state
    },
    removeUpload: uploadAdapter.removeOne,
    updateUpload: (state, action: PayloadAction<{ id: ID; progress: number }>) => {
      state.entities[action.payload.id].progress = action.payload.progress

      return state
    },
  },
})

export const {
  addUpload,
  removeUpload,
  failUpload,
  finishUpload,
  updateUpload,
} = uploadSlice.actions

